import axios from "axios";
import { Camera, Loader2, Play, Star, Trash2, Video } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { Switch } from "@/components/ui/switch";

import { urlMidia } from "./midiasPublicas";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const SEGUNDOS = 15;
const MAX_MB = 45;
export const MIDIAS_VAZIAS = { extras: [], video_destaque: false };
// GIF não é aceito (decisão do dono, 24/10/2026): pesa muito mais que o MP4 do
// mesmo trecho. Animação vai como vídeo. O servidor também recusa.
export const FOTO_ACEITA = "image/png,image/jpeg,image/webp";
export const SEM_GIF = "GIF não é aceito na foto. Para animação, envie um vídeo MP4 de até 15 s em \"Vídeo até 15s\": fica bem mais leve.";
export const ehGif = (arquivo) => arquivo?.type === "image/gif" || /\.gif$/i.test(arquivo?.name || "");
export const VIDEO_ACEITO = "video/mp4,video/quicktime,video/webm";
export const ehVideo = (arquivo) => String(arquivo?.type || "").startsWith("video/") || /\.(mp4|mov|webm)$/i.test(arquivo?.name || "");

/** Lê duração e tira a capa (quadro do meio do 1º segundo, recortado em quadrado). */
function lerVideo(arquivo) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(arquivo);
    const v = document.createElement("video");
    v.preload = "auto"; v.muted = true; v.playsInline = true; v.src = url;
    // Navegador que não decodifica o vídeo às vezes nem avisa: não deixa a tela presa
    let prazo = null;
    const limpar = () => { clearTimeout(prazo); URL.revokeObjectURL(url); };
    prazo = setTimeout(() => { limpar(); reject(new Error("Não consegui ler este vídeo. Tente outro, em MP4.")); }, 10000);
    v.onerror = () => { limpar(); reject(new Error("Não consegui abrir este vídeo. Tente MP4 ou MOV.")); };
    v.onloadedmetadata = () => {
      // Duração desconhecida (alguns WebM): segue, o servidor corta em 15 s
      const finita = Number.isFinite(v.duration);
      if (finita && v.duration > SEGUNDOS + 0.5) { limpar(); reject(new Error(`O vídeo tem ${Math.round(v.duration)} s. O limite é ${SEGUNDOS} s: corte no celular e envie de novo.`)); return; }
      v.currentTime = finita ? Math.min(0.5, v.duration / 2) : 0.5;
    };
    v.onseeked = () => {
      const lado = Math.min(v.videoWidth, v.videoHeight);
      const c = document.createElement("canvas");
      // 480 px, o tamanho do vídeo otimizado no servidor (24/10/2026)
      c.width = 480; c.height = 480;
      c.getContext("2d").drawImage(v, (v.videoWidth - lado) / 2, (v.videoHeight - lado) / 2, lado, lado, 0, 0, 480, 480);
      c.toBlob((b) => { limpar(); b ? resolve(new File([b], "capa.jpg", { type: "image/jpeg" })) : reject(new Error("Não consegui gerar a capa do vídeo.")); }, "image/jpeg", 0.85);
    };
  });
}

async function enviarFoto(arquivo) {
  const fd = new FormData(); fd.append("file", arquivo);
  const r = await axios.post("/api/upload/product-photo", fd, auth());
  return r.data?.photo_url;
}

/** Envia o vídeo do produto (capa + vídeo). Usado aqui e no quadro grande da
 *  foto 1 (Products.js). Devolve {url, poster, otimizado}; erro tem mensagem. */
export async function enviarVideo(arquivo, aoProgresso) {
  if (arquivo.size > MAX_MB * 1024 * 1024) throw new Error(`O vídeo passa de ${MAX_MB} MB. Grave um trecho menor.`);
  aoProgresso?.(0);
  const capa = await lerVideo(arquivo);
  const poster = await enviarFoto(capa);
  const fd = new FormData(); fd.append("file", arquivo);
  const r = await axios.post("/api/upload/product-video", fd, {
    ...auth(), onUploadProgress: (p) => p.total && aoProgresso?.(Math.round((p.loaded / p.total) * 100)),
  });
  return { url: r.data.video_url, poster, otimizado: !!r.data.otimizado };
}

/**
 * Fotos extras e vídeo do produto, abaixo da foto principal (pedido do dono,
 * 23/10/2026): até 3 mídias no total, no máximo 1 vídeo de até 15 s. Aparecem
 * só para o cliente (cardápio digital, Mesa QR e Totem). Com o vídeo como
 * destaque, ele vira a 1ª mídia e fica tocando no cartão do cardápio.
 */
/** Etiqueta "Foto 2" / "2 · Vídeo" no canto do quadro: deixa claro que são as fotos 1, 2 e 3 */
export function NumeroDaMidia({ n, tipo, vazio = false }) {
  const texto = vazio ? `Foto ${n}` : `${n} · ${tipo === "video" ? "Vídeo" : "Foto"}`;
  return (
    <span className={`absolute top-1.5 left-1.5 z-10 px-1.5 py-0.5 rounded-md text-[10px] font-bold leading-none ${vazio ? "bg-muted text-muted-foreground" : "bg-black/60 text-white"}`}>
      {texto}
    </span>
  );
}

/** O miolo padrão dos quadros vazios das fotos 1, 2 e 3 (dono, 24/10/2026: "um
 *  card padrão para todos"): câmera para foto e câmera de vídeo lado a lado. */
export function MidiaVazia({ soFoto = false, enviando = null }) {
  if (enviando !== null) {
    return (
      <span className="text-xs text-muted-foreground flex flex-col items-center gap-1.5">
        <Loader2 className="w-5 h-5 animate-spin text-orange-500" /> {enviando === "foto" ? "Enviando foto…" : `Enviando vídeo ${enviando}%`}
      </span>
    );
  }
  const quadrado = "w-10 h-10 rounded-xl bg-orange-500/10 text-orange-500 grid place-items-center";
  return (
    <span className="flex flex-col items-center gap-2 text-center">
      <span className="flex items-center gap-1.5">
        <span className={quadrado} title="Foto"><Camera className="w-5 h-5" /></span>
        {!soFoto && <span className={quadrado} title="Vídeo"><Video className="w-5 h-5" /></span>}
      </span>
      <span className="text-xs font-semibold leading-tight text-foreground">{soFoto ? "Foto" : `Foto ou vídeo até ${SEGUNDOS}s`}</span>
      <span className="text-[10px] text-muted-foreground leading-tight">{soFoto ? "PNG, JPG ou WEBP" : "PNG, JPG, WEBP ou MP4"}</span>
    </span>
  );
}

export default function MidiasExtras({ valor, onChange, temFotoPrincipal }) {
  const [enviando, setEnviando] = useState(null); // "foto" | percentual do vídeo
  const inputMidia = useRef(null);
  const extras = valor?.extras || [];
  const temVideo = extras.some((m) => m.tipo === "video");
  const vagas = Math.max(0, 2 - extras.length);
  const mudar = (novo) => onChange({ ...valor, ...novo });
  const tirar = (k) => {
    const lista = extras.filter((_, j) => j !== k);
    mudar({ extras: lista, video_destaque: valor.video_destaque && lista.some((m) => m.tipo === "video") });
  };

  const escolherFoto = async (arquivo) => {
    if (!arquivo) return;
    if (ehGif(arquivo)) { toast.error(SEM_GIF); return; }
    setEnviando("foto");
    try {
      const url = await enviarFoto(arquivo);
      if (url) mudar({ extras: [...extras, { tipo: "foto", url }] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível enviar a foto");
    } finally {
      setEnviando(null);
    }
  };

  // Um cartão só para os dois: o tipo do arquivo decide se é foto ou vídeo
  const escolher = (arquivo) => {
    if (!arquivo) return;
    if (!ehVideo(arquivo)) return escolherFoto(arquivo);
    if (temVideo) { toast.error("Cada produto tem 1 vídeo só. Para este espaço, envie uma foto."); return; }
    return escolherVideo(arquivo);
  };

  const escolherVideo = async (arquivo) => {
    if (!arquivo) return;
    try {
      const v = await enviarVideo(arquivo, setEnviando);
      mudar({ extras: [...extras, { tipo: "video", url: v.url, poster: v.poster }], video_destaque: true });
      toast.success(v.otimizado ? "Vídeo enviado e otimizado" : "Vídeo enviado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Não foi possível enviar o vídeo");
    } finally {
      setEnviando(null);
    }
  };

  return (
    <div className="mt-3 space-y-2" data-e2e="midias-extras">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Fotos 2 e 3 · só para o cliente</p>
      <div className="grid grid-cols-2 gap-2">
        {extras.map((m, k) => (
          <div key={m.url} className="relative aspect-square rounded-xl overflow-hidden border bg-muted/40">
            <NumeroDaMidia n={k + 2} tipo={m.tipo} />
            <img src={urlMidia(m.tipo === "video" ? m.poster : m.url)} alt="" className="w-full h-full object-cover" loading="lazy" />
            {m.tipo === "video" && <span className="absolute inset-0 grid place-items-center"><span className="w-9 h-9 rounded-full bg-black/55 grid place-items-center text-white"><Play className="w-4 h-4 ml-0.5" /></span></span>}
            <button type="button" onClick={() => tirar(k)} title="Tirar" className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/55 text-white grid place-items-center hover:bg-red-600">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {/* Os espaços livres (até 2 mídias extras), todos iguais (dono, 24/10/2026) */}
        {Array.from({ length: vagas }, (_, k) => (
          <button key={`vaga-${k}`} type="button" disabled={enviando !== null} onClick={() => inputMidia.current?.click()}
            data-e2e="midia-vaga"
            className="relative aspect-square rounded-xl border-2 border-dashed border-border/60 grid place-items-center p-2 text-center hover:bg-muted/60 hover:border-orange-400/60 transition-colors disabled:cursor-wait">
            <NumeroDaMidia n={extras.length + k + 2} vazio />
            <MidiaVazia soFoto={temVideo} enviando={k === 0 ? enviando : null} />
          </button>
        ))}
      </div>
      {temVideo && (
        <label className="flex items-center justify-between gap-2 text-xs rounded-lg bg-muted/40 px-2.5 py-2">
          <span className="flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-amber-500" /> Vídeo como destaque<span className="text-muted-foreground">(toca no cardápio)</span></span>
          <Switch checked={!!valor.video_destaque} onCheckedChange={(v) => mudar({ video_destaque: v })} />
        </label>
      )}
      {!temFotoPrincipal && extras.length > 0 && <p className="text-[11px] text-amber-600">Coloque também a foto principal: é ela que aparece nas outras telas.</p>}
      <input ref={inputMidia} type="file" accept={temVideo ? FOTO_ACEITA : `${FOTO_ACEITA},${VIDEO_ACEITO}`} className="hidden"
        onChange={(e) => { escolher(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}
